import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusCircle, Pencil, Trash2, Search, Eye, MoreHorizontal } from "lucide-react";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { accountApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";

// Account type to match API response
type Account = {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  type: string;
  visibility: string;
  openingBalance: number;
  balanceType: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
};

// API response types
type ListResponse = {
  docs: Account[];
  totalDocs: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

type SingleResponse = {
  data: Account;
  message: string;
};

type CreateUpdateResponse = {
  data: string;
  message: string;
};

type DeleteResponse = {
  message: string;
};

const ITEMS_PER_PAGE = 10;

// Account type options with key-value pairs
const ACCOUNT_TYPES = [
  { key: "income", value: "Income" },
  { key: "expense", value: "Expense" },
  { key: "both", value: "Both" },
  { key: "cash", value: "Cash" },
  { key: "bank", value: "Bank" },
];

// Balance type options with key-value pairs
const BALANCE_TYPES = [
  { key: "receivable", value: "Receivable" },
  { key: "payable", value: "Payable" },
];

// Visibility options with key-value pairs
const VISIBILITY_OPTIONS = [
  { key: "payment", value: "Payment" },
  { key: "receipt", value: "Receipt" },
];

const AccountsPage = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    type: "expense",
    visibility: "payment",
    openingBalance: 0,
    balanceType: "payable",
    status: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: "all",
    status: "all",
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
  const [viewAccount, setViewAccount] = useState<Account | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { toast } = useToast();
  const { user } = useAuth();

  // Load data from API on initial render and when filters/pagination change
  useEffect(() => {
    fetchAccounts();
  }, [currentPage, filters.query, filters.type, filters.status]);

  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const response = await accountApi.getAccounts({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query.trim() || undefined,
        type: filters.type !== "all" ? filters.type : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
      });

      setAccounts(response.docs);
      setTotalDocs(response.totalDocs);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to fetch accounts",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: string,
    value: string | number | boolean
  ) => {
    if (field === "name") {
      // Capitalize first letter of each word
      const capitalizedValue = (value as string)
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData({ ...formData, [field]: capitalizedValue });
    } else if (field === "openingBalance") {
      // Handle opening balance input
      const numValue =
        typeof value === "string"
          ? parseFloat(value) || 0
          : typeof value === "number"
          ? value
          : 0;
      setFormData({ ...formData, [field]: numValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleEdit = (account: Account) => {
    setFormData({
      name: account.name.trim(),
      code: account.code.trim(),
      type: account.type,
      visibility: account.visibility,
      openingBalance: account.openingBalance,
      balanceType: account.balanceType,
      status: account.status,
    });
    setEditingId(account.id);
    setShowForm(true);
  };

  const handleDeleteClick = (id: string) => {
    setAccountToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;

    try {
      setIsLoading(true);
      await accountApi.deleteAccount(accountToDelete);

      fetchAccounts(); // Refresh the list
      toast({
        title: "Success",
        description: "Account deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to delete account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleAdd = async () => {
    // Trim fields before validation
    const trimmedName = formData.name.trim();
    const trimmedCode = formData.code.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await accountApi.createAccount({
        ...formData,
        name: trimmedName
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" "),
        code: trimmedCode,
        organizationId: user?.organizationId || "",
      });

      fetchAccounts(); // Refresh the list
      clearFormState();
      setShowForm(false);
      toast({
        title: "Success",
        description: "Account added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to add account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    // Trim fields before validation
    const trimmedName = formData.name.trim();
    const trimmedCode = formData.code.trim();

    if (!trimmedName || !editingId) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      await accountApi.updateAccount(editingId, {
        ...formData,
        name: trimmedName
          .split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" "),
        code: trimmedCode,
        organizationId: user?.organizationId || "",
      });

      fetchAccounts(); // Refresh the list
      clearFormState();
      setShowForm(false);
      toast({
        title: "Success",
        description: "Account updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ? error.message : "Failed to update account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      handleUpdate();
    } else {
      handleAdd();
    }
  };

  // Calculate total pages based on totalDocs from API
  const totalPages = Math.ceil(totalDocs / ITEMS_PER_PAGE);

  // Handle filter changes with trimmed search value
  const handleFilterChange = (field: string, value: string) => {
    const trimmedValue = field === "query" ? value.trim() : value;
    setFilters((prev) => ({ ...prev, [field]: trimmedValue }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleView = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await accountApi.getAccount(id);
      setViewAccount(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message
          ? error.message
          : "Failed to fetch account details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearFormState = () => {
    setFormData({
      name: "",
      code: "",
      type: "expense",
      visibility: "payment",
      openingBalance: 0,
      balanceType: "payable",
      status: true,
    });
    setEditingId(null);
  };

  return (
    <MastersLayout
    // title="Accounts"
    // description="Manage system accounts and their configurations"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search accounts..."
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Select
              value={filters.type}
              onValueChange={(value) => handleFilterChange("type", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ACCOUNT_TYPES.map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    {type.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setFilters({ type: "all", status: "all", query: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card> */}
          {/* <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Accounts</CardTitle>
                <CardDescription>
                  Manage system accounts and their configurations
                </CardDescription>
              </div>
              <Button onClick={() => setShowForm(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Account
              </Button>
            </div>
          {/* </CardHeader> */}
          {/* <CardContent> */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="hidden md:table-cell">Type</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Visibility
                  </TableHead>
                  <TableHead>Opening Balance</TableHead>
                  <TableHead>Balance Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-4 text-muted-foreground"
                    >
                      {filters.query || filters.type || filters.status
                        ? "No accounts match the selected filters"
                        : "No accounts found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">
                        {account.name}
                      </TableCell>
                      <TableCell>{account.code}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {ACCOUNT_TYPES.find((t) => t.key === account.type)
                          ?.value || account.type}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {VISIBILITY_OPTIONS.find(
                          (v) => v.key === account.visibility
                        )?.value || account.visibility}
                      </TableCell>
                      <TableCell>{account.openingBalance}</TableCell>
                      <TableCell>{account.balanceType}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`status-${account.id}`}
                            checked={account.status}
                            onCheckedChange={async (checked) => {
                              try {
                                setIsLoading(true);
                                await accountApi.updateAccount(account.id, {
                                  ...account,
                                  status: checked,
                                });
                                fetchAccounts(); // Refresh the list
                                toast({
                                  title: "Success",
                                  description: `Account status updated to ${
                                    checked ? "Active" : "Inactive"
                                  }`,
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description: error.message
                                    ? error.message
                                    : "Failed to update account status",
                                  variant: "destructive",
                                });
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                          />
                          <span>{account.status ? "Active" : "Inactive"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={() => handleView(account.id)}
                              className="cursor-pointer"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleEdit(account)}
                              className="cursor-pointer"
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(account.id)}
                              className="cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {totalDocs > ITEMS_PER_PAGE && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          {/* </CardContent>
        </Card> */}
      </div>

      {showForm && (
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            if (!open) {
              clearFormState();
            }
            setShowForm(open);
          }}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit" : "Add New"} Account
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the account information below"
                  : "Fill in the account information below"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Account Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter account name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Account Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => handleInputChange("code", e.target.value)}
                    placeholder="Enter account code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleInputChange("type", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="visibility">Visibility</Label>
                  <Select
                    value={formData.visibility}
                    onValueChange={(value) =>
                      handleInputChange("visibility", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIBILITY_OPTIONS.map((option) => (
                        <SelectItem key={option.key} value={option.key}>
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="openingBalance">Opening Balance</Label>
                  <div className="flex gap-2">
                    <Input
                      id="openingBalance"
                      type="number"
                      step="0.01"
                      min={0}
                      value={
                        formData.openingBalance === 0
                          ? ""
                          : formData.openingBalance
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "openingBalance",
                          e.target.value === "" ? 0 : e.target.value
                        )
                      }
                      placeholder="Enter opening balance"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="balanceType">Balance Type</Label>
                  <Select
                    value={formData.balanceType}
                    onValueChange={(value) =>
                      handleInputChange("balanceType", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select balance type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BALANCE_TYPES.map((type) => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="status"
                      checked={formData.status}
                      onCheckedChange={(checked) =>
                        handleInputChange("status", checked)
                      }
                    />
                    <span>{formData.status ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({
                      name: "",
                      code: "",
                      type: "expense",
                      visibility: "payment",
                      openingBalance: 0,
                      balanceType: "payable",
                      status: true,
                    });
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Create"} Account
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure want to delete the account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Account Details</DialogTitle>
          </DialogHeader>
          {viewAccount && (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="text-sm">{viewAccount.name}</p>
                </div>
                <div>
                  <Label>Code</Label>
                  <p className="text-sm">{viewAccount.code}</p>
                </div>
                <div>
                  <Label>Type</Label>
                  <p className="text-sm">
                    {ACCOUNT_TYPES.find((t) => t.key === viewAccount.type)
                      ?.value || viewAccount.type}
                  </p>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <p className="text-sm">
                    {VISIBILITY_OPTIONS.find(
                      (v) => v.key === viewAccount.visibility
                    )?.value || viewAccount.visibility}
                  </p>
                </div>
                <div>
                  <Label>Opening Balance</Label>
                  <p className="text-sm">{viewAccount.openingBalance}</p>
                </div>
                <div>
                  <Label>Balance Type</Label>
                  <p className="text-sm">
                    {BALANCE_TYPES.find(
                      (b) => b.key === viewAccount.balanceType
                    )?.value || viewAccount.balanceType}
                  </p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p className="text-sm">
                    {viewAccount.status ? "Active" : "Inactive"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MastersLayout>
  );
};

export default AccountsPage;
